import { useEffect, useState } from 'react';
import { Button, Empty, message, Modal, Table, Tabs, Tag, Typography } from 'antd';
import { CopyOutlined, DownloadOutlined, RobotOutlined, UploadOutlined } from '@ant-design/icons';
import { collection, getDocs, query, where } from 'firebase/firestore';
import ReactMarkdown from 'react-markdown';
import { ESmsRole } from '@leedpulse-monorepo/enums';
import { firestore, FirestoreCollections } from '../../../firebase.config';
import ChatBubble from './ChatBubble';

interface CallsListProps {
  contactId: string;
}

interface CallRow {
  id: string;
  customer: string;
  sender: string;
  duration: number;
  transcription: string;
  direction: string;
  created: number;
  missed: boolean;
  urlRecording: string | null;
  transcriptionSrt: string | null;
  platform: string;
}

const formatDuration = (seconds: number): string => {
  if (!seconds || seconds <= 0) return '-';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}min ${s}s` : `${s}s`;
};

const normalizeTranscription = (value: unknown): string => {
  if (typeof value === 'string') return value;
  if (value && typeof value === 'object') {
    return Object.values(value as Record<string, unknown>)
      .map((v) => (typeof v === 'string' ? v : JSON.stringify(v)))
      .join(' — ');
  }
  return '-';
};

interface SrtCue {
  speaker: string;
  text: string;
}

const parseSrtCues = (srt: string): SrtCue[] => {
  const speakerRegex = /^\[(.+?)\]\s*$/;
  return srt
    .trim()
    .split(/\n\s*\n/)
    .map((block) => {
      const lines = block.trim().split('\n');
      if (lines.length < 3) return null;
      const remaining = lines.slice(2);
      const speakerMatch = remaining[0]?.match(speakerRegex);
      const speaker = speakerMatch ? speakerMatch[1] : '';
      const textLines = speakerMatch ? remaining.slice(1) : remaining;
      const text = textLines.join(' ').trim();
      return text ? { speaker, text } : null;
    })
    .filter((cue): cue is SrtCue => cue !== null);
};

const extractSpeakerName = (speaker: string): string => {
  const name = speaker.split(' - ')[0]?.trim();
  return name || 'Interlocuteur';
};

const renderChannelTag = (platform: string) => (
  <>
    {platform === 'callr' ? <Tag>GSM</Tag> : <Tag color="green">VoIP</Tag>}
    {platform === 'elevenlabs_receptionist' && (
      <Tag icon={<RobotOutlined />} color="purple">
        Réceptionniste IA
      </Tag>
    )}
  </>
);

const CallsList: React.FC<CallsListProps> = ({ contactId }) => {
  const [calls, setCalls] = useState<CallRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedCall, setSelectedCall] = useState<CallRow | null>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);

  useEffect(() => {
    const fetchCalls = async () => {
      setLoading(true);
      try {
        const snapshot = await getDocs(
          query(
            collection(firestore, FirestoreCollections.calls),
            where('id_contact', '==', contactId)
          )
        );
        const rows: CallRow[] = snapshot.docs.map((docSnap) => {
          const d = docSnap.data();
          return {
            id: docSnap.id,
            customer: d.customer ?? '-',
            sender: d.sender ?? '-',
            duration: d.duration ?? 0,
            transcription: normalizeTranscription(d.transcription),
            direction: d.direction ?? '-',
            created: d.created ?? 0,
            missed: d.missed ?? false,
            urlRecording: d.url_recording ?? null,
            transcriptionSrt: d.transcription_srt && d.transcription_srt !== '-' ? d.transcription_srt : null,
            platform: d.platform ?? '-',
          };
        });
        setCalls(rows.sort((a, b) => b.created - a.created));
      } finally {
        setLoading(false);
      }
    };
    fetchCalls();
  }, [contactId]);

  const handleViewDetails = (record: CallRow) => {
    setSelectedCall(record);
    setIsModalVisible(true);
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
  };

  const handleCopySrt = async () => {
    if (!selectedCall?.transcriptionSrt) return;
    try {
      await navigator.clipboard.writeText(selectedCall.transcriptionSrt);
      message.success('Transcription SRT copiée dans le presse-papiers');
    } catch {
      message.error('Copie impossible (presse-papiers indisponible)');
    }
  };

  const srtCues = selectedCall?.transcriptionSrt ? parseSrtCues(selectedCall.transcriptionSrt) : [];
  const srtSpeakers = [...new Set(srtCues.map((cue) => extractSpeakerName(cue.speaker)))];
  const isAgentSpeaker = (speakerName: string): boolean =>
    /agent/i.test(speakerName) || (srtSpeakers.length === 2 && srtSpeakers.indexOf(speakerName) === 0);

  const columns = [
    {
      title: 'Date',
      dataIndex: 'created',
      key: 'created',
      render: (created: number) =>
        created ? new Date(created).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '-',
    },
    {
      title: 'Type',
      dataIndex: 'direction',
      key: 'direction',
      render: (direction: string) => {
        if (direction === 'inbound') {
          return <Tag icon={<DownloadOutlined />} color="blue">Entrant</Tag>;
        }
        if (direction === 'outbound') {
          return <Tag icon={<UploadOutlined />} color="green">Sortant</Tag>;
        }
        return '-';
      },
    },
    {
      title: 'Canal',
      dataIndex: 'platform',
      key: 'platform',
      render: (platform: string) => renderChannelTag(platform),
    },
    {
      title: 'N° appelant',
      dataIndex: 'customer',
      key: 'customer',
    },
    {
      title: 'N° appelé',
      dataIndex: 'sender',
      key: 'sender',
    },
    {
      title: 'Durée',
      dataIndex: 'duration',
      key: 'duration',
      render: (duration: number) => formatDuration(duration),
    },
    {
      title: 'Manqué',
      dataIndex: 'missed',
      key: 'missed',
      render: (missed: boolean) => (missed ? <Tag color="red">Manqué</Tag> : '-'),
    },
    {
      title: 'Résumé',
      dataIndex: 'transcription',
      key: 'transcription',
      render: (text: string) => (
        <Typography.Text ellipsis={{ tooltip: text }} style={{ maxWidth: 200, display: 'block' }}>
          {text && text !== '-' ? text : '-'}
        </Typography.Text>
      ),
    },
  ];

  return (
    <>
      <Table
        rowKey="id"
        dataSource={calls}
        columns={columns}
        loading={loading}
        pagination={{ pageSize: 10, showSizeChanger: false }}
        size="small"
        scroll={{ x: true }}
        onRow={(record) => ({
          onClick: () => handleViewDetails(record),
          style: { cursor: 'pointer' },
        })}
        locale={{ emptyText: <Empty description='Aucun appel disponible'></Empty> }}
      />
      <Modal
        title="Détails de l'appel"
        open={isModalVisible}
        onCancel={handleCloseModal}
        destroyOnHidden={true}
        footer={[
          <Button key="close" onClick={handleCloseModal}>
            Fermer
          </Button>,
        ]}
      >
        {selectedCall && (
          <Tabs defaultActiveKey='1'>
            <Tabs.TabPane key={1} tab='Détails'>
            <div style={{ marginBottom: '16px' }}>
              <strong>Date :</strong>
              <div style={{ marginTop: '4px' }}>
                {selectedCall.created
                  ? new Date(selectedCall.created).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })
                  : '-'}
              </div>
            </div>
            <div style={{ marginBottom: '16px' }}>
              <strong>Type :</strong>
              <div style={{ marginTop: '4px' }}>
                {selectedCall.direction === 'inbound'
                  ? 'Entrant'
                  : selectedCall.direction === 'outbound'
                    ? 'Sortant'
                    : '-'}
              </div>
            </div>
            <div style={{ marginBottom: '16px' }}>
              <strong>Canal :</strong>
              <div style={{ marginTop: '4px' }}>{renderChannelTag(selectedCall.platform)}</div>
            </div>
            <div style={{ marginBottom: '16px' }}>
              <strong>N° appelant :</strong>
              <div style={{ marginTop: '4px' }}>{selectedCall.customer}</div>
            </div>
            <div style={{ marginBottom: '16px' }}>
              <strong>N° appelé :</strong>
              <div style={{ marginTop: '4px' }}>{selectedCall.sender}</div>
            </div>
            <div style={{ marginBottom: '16px' }}>
              <strong>Durée :</strong>
              <div style={{ marginTop: '4px' }}>{formatDuration(selectedCall.duration)}</div>
            </div>
            <div style={{ marginBottom: '16px' }}>
              <strong>Manqué :</strong>
              <div style={{ marginTop: '4px' }}>{selectedCall.missed ? 'Oui' : 'Non'}</div>
            </div>
            {/* `url_recording` porte une sentinelle (`pending`, `not_available`,
                `not_recorded`) sur les appels dont le fichier n'est pas à cette
                racine — notamment ceux basculés en messagerie, où le contenu vit
                dans `Voicemails`. Sans ce test le lien pointait sur la sentinelle
                elle-même et ne menait nulle part. */}
            {selectedCall.urlRecording?.startsWith('http') && (
              <div style={{ marginBottom: '16px' }}>
                <strong>Enregistrement :</strong>
                <div style={{ marginTop: '4px' }}>
                  <a href={selectedCall.urlRecording} target="_blank" rel="noreferrer">
                    Écouter l'enregistrement
                  </a>
                </div>
              </div>
            )}
            <div style={{ marginBottom: '16px' }}>
              <strong>Résumé :</strong>
              <div style={{ marginTop: '4px', wordBreak: 'break-word' }}>
                <ReactMarkdown>{selectedCall.transcription}</ReactMarkdown>
              </div>
            </div>
            </Tabs.TabPane>
            {selectedCall.transcriptionSrt && (
              <Tabs.TabPane key={2} tab='Transcription SRT'>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '8px' }}>
                  <Button icon={<CopyOutlined />} onClick={handleCopySrt} size="small">
                    Copier le SRT brut
                  </Button>
                </div>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    maxHeight: '50vh',
                    overflow: 'auto',
                    padding: '4px',
                  }}
                >
                  {srtCues.map((cue, index) => {
                    const speakerName = extractSpeakerName(cue.speaker);
                    return (
                      <ChatBubble
                        key={index}
                        message={cue.text}
                        date={speakerName}
                        role={isAgentSpeaker(speakerName) ? ESmsRole.assistant : ESmsRole.user}
                        manualSending={false}
                      />
                    );
                  })}
                </div>
              </Tabs.TabPane>
            )}
          </Tabs>
        )}
      </Modal>
    </>
  );
};

export default CallsList;
